import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { FileText, Download, ScanLine, Calendar, User, Sparkles, FileDown } from "lucide-react";
import TaxBadge from "@/components/app/TaxBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtSize(bytes) {
  if (!bytes) return "0 B";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
}

export default function SharedFolder() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/shared/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Link non valido"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm">Caricamento…</div>;
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F6] p-8">
        <div className="max-w-md text-center bg-white border border-black/10 rounded-xl p-10">
          <div className="w-14 h-14 mx-auto bg-[#FF3333]/10 rounded-xl flex items-center justify-center mb-4">
            <FileText className="w-6 h-6 text-[#FF3333]" />
          </div>
          <h1 className="font-display font-bold text-2xl mb-2 tracking-tight">Link non disponibile</h1>
          <p className="text-sm text-[#5C5C5C]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F6]">
      <header className="border-b border-black/10 bg-white/70 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#0A0A0A] flex items-center justify-center rounded-xl">
              <ScanLine className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold tracking-tighter">Archivio Smart</span>
          </div>
          <Link to="/" className="text-xs text-[#5C5C5C] hover:text-[#0A0A0A] transition-colors">Scopri di più →</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-10">
          <div className="overline text-[#0A0A0A] mb-3">— CARTELLA CONDIVISA</div>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">
              {data.folder.name}
            </h1>
            {data.count > 0 && (
              <a
                href={`${API}/shared/${token}/zip`}
                target="_blank"
                rel="noreferrer"
                data-testid="shared-zip-download"
                className="flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-11 px-5 rounded-xl text-sm font-medium transition-colors"
              >
                <FileDown className="w-4 h-4" /> Scarica tutto (ZIP)
              </a>
            )}
          </div>
          <div className="flex flex-wrap gap-6 text-sm text-[#5C5C5C]">
            <div className="flex items-center gap-2"><User className="w-4 h-4" /> Condivisa da <b className="text-[#0A0A0A]">{data.owner_name}</b></div>
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Valido fino al {fmtDate(data.expires_at)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10 rounded-xl mb-10 overflow-hidden">
          <div className="bg-white p-6">
            <div className="overline text-[#8A8A8A] mb-4">DOCUMENTI</div>
            <div className="font-display font-black text-4xl tracking-tighter">{data.count}</div>
          </div>
          <div className="bg-white p-6">
            <div className="overline text-[#8A8A8A] mb-4">TOTALE ESTRATTO</div>
            <div className="font-display font-black text-4xl tracking-tighter">€ {Number(data.total_amount || 0).toFixed(2)}</div>
          </div>
          <div className="bg-white p-6">
            <div className="overline text-[#8A8A8A] mb-4">TIPOLOGIA</div>
            <div className="font-display font-black text-2xl tracking-tighter flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#009966]" />
              Sez. Fiscale
            </div>
          </div>
        </div>

        {data.count === 0 ? (
          <div className="border border-dashed border-black/15 rounded-xl p-16 text-center bg-white">
            <FileText className="w-12 h-12 text-black/20 mx-auto mb-4" />
            <p className="text-sm text-[#8A8A8A]">Nessun documento in questa cartella.</p>
          </div>
        ) : (
          <div className="bg-white border border-black/10 rounded-xl overflow-hidden">
            <div className="border-b border-black/10 px-6 py-4 grid grid-cols-12 overline text-[#8A8A8A] text-[10px]">
              <div className="col-span-5">DOCUMENTO</div>
              <div className="col-span-2">ESERCENTE</div>
              <div className="col-span-2">DATA</div>
              <div className="col-span-2 text-right">IMPORTO</div>
              <div className="col-span-1 text-right">SCARICA</div>
            </div>
            {data.documents.map(d => (
              <div
                key={d.id}
                data-testid={`shared-doc-${d.id}`}
                className="grid grid-cols-12 px-6 py-4 border-b border-black/5 last:border-b-0 hover:bg-black/5 transition-colors text-sm items-center"
              >
                <div className="col-span-5 flex items-center gap-3 min-w-0">
                  <FileText className="w-4 h-4 shrink-0 text-[#0A0A0A]" />
                  <span className="truncate font-medium">{d.smart_filename}</span>
                  <TaxBadge relevance={d.tax_relevance} withIcon={false} />
                </div>
                <div className="col-span-2 text-xs text-[#5C5C5C] truncate">{d.vendor || "—"}</div>
                <div className="col-span-2 text-xs text-[#5C5C5C] font-mono-plex">{d.document_date || "—"}</div>
                <div className="col-span-2 text-right font-mono-plex">
                  {d.total_amount != null ? `€ ${Number(d.total_amount).toFixed(2)}` : "—"}
                </div>
                <div className="col-span-1 flex justify-end">
                  <a
                    href={`${API}/shared/${token}/documents/${d.id}/file`}
                    download={d.smart_filename}
                    target="_blank"
                    rel="noreferrer"
                    className="p-1.5 rounded-xl hover:bg-black/5 transition-colors"
                    data-testid={`shared-download-${d.id}`}
                    title="Scarica"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-16 border-t border-black/10 pt-6 text-xs text-[#8A8A8A] text-center">
          Ricevuto da <b>Archivio Smart</b> — link protetto, non condividere pubblicamente.
        </div>
      </main>
    </div>
  );
}
